import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { triggerIntegrationActions } from 'api/integrations';
import { ThankViewMappingItem } from 'assets/js/pages/account-settings/account-integrations/ThankView/ThankViewMappingItem';
import { THANKVIEW_INTEGRATION_NOT_FOUND_ERROR } from 'assets/js/pages/account-settings/account-integrations/constants';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountIntegrations } from 'customHooks/useCurrentAccountIntegrations';
import { useInfiniteCampaigns } from 'customHooks/useInfiniteCampaigns';
import { Integration } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const ThankViewMapping = () => {
    const { success, error } = useToast();
    const { accountId } = useCurrentAccount();
    const { data: integrations } = useCurrentAccountIntegrations();
    const { thankview } = integrations || {};

    const {
        data: campaignData,
        ref: infiniteScrollRef,
        isFetchingNextPage,
    } = useInfiniteCampaigns();
    const campaigns = campaignData?.pages ?? [];

    const { mutate: refresh, isPending: isRefreshing } = useMutation<
        Integration,
        GivebutterAxiosError
    >({
        mutationFn: () => {
            if (!thankview?.id) {
                throw new Error(THANKVIEW_INTEGRATION_NOT_FOUND_ERROR);
            }

            return triggerIntegrationActions(accountId, thankview.id, ['project.sync']);
        },
        onSuccess: () => {
            success('ThankView Mapping refreshed successfully!');
        },
        onError: e => {
            error(e.response?.data?.message || 'Something went wrong. Please try again.');
        },
    });

    return (
        <>
            <p className="text-sm font-medium">Campaign Mapping</p>
            <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
                <p className="flex-shrink text-sm">
                    Map your Givebutter campaigns to the appropriate campaigns in ThankView.
                </p>

                <Button
                    variant="outline"
                    color="neutral"
                    size="sm"
                    onClick={() => refresh()}
                    disabled={isRefreshing}
                >
                    <FontAwesomeIcon
                        icon={faRefresh}
                        className={isRefreshing ? 'animate-spin' : ''}
                    />
                    Refresh
                </Button>
            </div>
            {thankview ? (
                <div className="flex flex-col gap-2">
                    {campaigns.map(campaign => (
                        <ThankViewMappingItem key={campaign.id} campaign={campaign} />
                    ))}
                    <div ref={infiniteScrollRef} className="h-4" />
                    {isFetchingNextPage ? (
                        <div className="flex items-center justify-center p-4">
                            <Loader variant={'static'} />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

export { ThankViewMapping };
